import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import {
  Award,
  CalendarDays,
  Layers,
  Loader2,
  Repeat,
  Shield,
  Sparkles,
  Target,
  Upload,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Checkbox } from '@/components/ui/checkbox'

import { BadgeMedallion } from './BadgeMedallion'
import { criterionTargetMissing } from './criterion-utils'
import { parseRosterFile } from './parse-roster-file'
import {
  CRITERION_GROUPS,
  MONTHS,
  POINT_OPTIONS,
  RECURRENCE_OPTIONS,
  TIERS,
  WEEKDAYS,
  WINDOW_OPTIONS,
} from './options'
import { useCompanies, type Company } from '@/services/admin/companies.service'
import { useAdminQuizzes, type Quiz } from '@/services/admin/quiz.service'
import { useAdminCourses, type Course } from '@/services/admin/courses.service'
import { useAdminModules, type Module } from '@/services/admin/modules.service'
import { useAdminEpisodes, type Episode } from '@/services/admin/episodes.service'
import { useUploadBadgeIcon } from '@/services/upload.service'
import { useToast } from '@/hooks/use-toast'
import { getErrorMessage } from '@/utils/error'
import { formatBrDateDashed, toDateInputValue } from './format'
import type {
  Badge,
  BadgeDraft,
  Criterion,
  CriterionType,
  Recurrence,
  Scope,
  TemporalWindow,
  Tier,
  WindowType,
} from './types'
import './styles.css'

interface BadgeFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  badge?: Badge | null
  onSubmit?: (draft: BadgeDraft, recipientEmails?: string[]) => void
}

const blankDraft: BadgeDraft = {
  name: '',
  description: '',
  howToEarn: '',
  iconUrl: null,
  tier: 'bronze',
  points: 5,
  criterion: { type: 'count_modules', count: 1 },
  window: { type: 'always' },
  recurrence: 'once',
  scope: { type: 'global' },
  active: true,
  order: 0,
}

const tierLabel: Record<Tier, string> = {
  bronze: 'Bronze',
  prata: 'Prata',
  ouro: 'Ouro',
  platina: 'Platina',
}

export function BadgeFormDialog({ open, onOpenChange, badge, onSubmit }: BadgeFormDialogProps) {
  const [draft, setDraft] = useState<BadgeDraft>(blankDraft)
  // Emails do arquivo de roster (critério manual_roster) — estado
  // transitório, não faz parte do draft; vai pro onSubmit em separado.
  const [recipientEmails, setRecipientEmails] = useState<string[]>([])
  // Lista de empresas pra popular o select de escopo company.
  // React Query cacheia entre aberturas do dialog.
  const { data: companies } = useCompanies()
  // Quizzes do back — alimenta o select do critério `quiz_perfect` E a
  // pré-visualização lateral (describeCriterion mostra título em vez
  // do ID cru).
  const { data: quizzes } = useAdminQuizzes()
  // Catálogo (curso/módulo/episódio) pra resolver os títulos na
  // pré-visualização. Os selects do critério usam os mesmos hooks —
  // React Query deduplica pela queryKey, sem requisição duplicada.
  const { data: courses } = useAdminCourses()
  const { data: modules } = useAdminModules()
  const { data: episodes } = useAdminEpisodes()

  // Upload de ícone PNG transparente (resize 256×256 no servidor PHP)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadMutation = useUploadBadgeIcon()
  const { toast } = useToast()

  async function handleIconFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const url = await uploadMutation.mutateAsync(file)
      patch('iconUrl', url)
      toast({
        title: 'Ícone enviado',
        description: 'Aparece no preview ao lado do nome.',
      })
    } catch (err: unknown) {
      toast({
        variant: 'destructive',
        title: 'Erro ao enviar ícone',
        description: getErrorMessage(err, 'Falha no upload do PNG.'),
      })
    } finally {
      // Limpa input pra permitir re-upload do MESMO arquivo se quiser
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  useEffect(() => {
    if (!open) return
    setRecipientEmails([])
    if (badge) {
      const { id: _id, createdAt: _c, updatedAt: _u, earnedCount: _e, ...editable } = badge
      setDraft(editable)
    } else {
      setDraft(blankDraft)
    }
  }, [open, badge])

  function patch<K extends keyof BadgeDraft>(key: K, value: BadgeDraft[K]) {
    setDraft((d) => ({ ...d, [key]: value }))
  }

  function changeTier(tier: Tier) {
    const def = TIERS.find((t) => t.value === tier)?.defaultPoints ?? 5
    setDraft((d) => ({ ...d, tier, points: def }))
  }

  function changeCriterionType(type: CriterionType) {
    let next: Criterion
    switch (type) {
      case 'specific_episode':
      case 'specific_module':
      case 'specific_course':
        next = { type, targetId: '' }
        break
      case 'count_episodes':
      case 'count_modules':
      case 'count_courses':
        next = { type, count: 1 }
        break
      case 'streak_days':
        next = { type, count: 5 }
        break
      case 'day_marathon':
        next = { type, count: 3 }
        break
      case 'quiz_perfect_streak':
        next = { type, count: 3 }
        break
      case 'return_after_idle':
        next = { type, count: 7 }
        break
      case 'any_activity':
      case 'account_created':
      case 'quiz_comeback':
        next = { type }
        break
      case 'quiz_perfect':
        next = { type }
        break
      case 'module_all_quizzes_perfect':
        next = { type }
        break
      case 'manual_roster':
        next = { type }
        break
    }
    setDraft((d) => ({
      ...d,
      criterion: next,
      // manual_roster não tem eixo temporal — janela/recorrência fixas.
      ...(type === 'manual_roster'
        ? { window: { type: 'always' } as TemporalWindow, recurrence: 'once' as Recurrence }
        : {}),
    }))
  }

  function changeWindowType(type: WindowType) {
    let next: TemporalWindow
    switch (type) {
      case 'always':
        next = { type }
        break
      case 'between': {
        const today = new Date().toISOString().slice(0, 10)
        next = { type, start: today, end: today }
        break
      }
      case 'specific_date':
        next = { type, date: new Date().toISOString().slice(0, 10) }
        break
      case 'recurring_yearly':
        next = { type, monthDay: '12-25' }
        break
      case 'recurring_monthly':
        next = { type, dayOfMonth: 1 }
        break
      case 'recurring_weekly':
        next = { type, weekday: 5 }
        break
    }
    setDraft((d) => ({ ...d, window: next }))
  }

  function handleSubmit() {
    // Guard: critério specific_* sem alvo → o back rejeita com 400.
    // Bloqueia aqui pra não enviar um payload que já se sabe inválido.
    if (criterionTargetMissing(draft.criterion)) return
    onSubmit?.(draft, recipientEmails.length > 0 ? recipientEmails : undefined)
    onOpenChange(false)
  }

  const inscription = draft.name?.trim() || 'NOVA'
  // Critérios specific_* exigem um alvo selecionado; sem ele o submit
  // fica travado (botão desabilitado + aviso no rodapé).
  const targetMissing = criterionTargetMissing(draft.criterion)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border/60">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Award className="w-5 h-5 text-primary" />
            {badge ? 'Editar Badge' : 'Nova Badge'}
          </DialogTitle>
          <DialogDescription>
            Combine critério, janela e recorrência. Sem persistência ainda — backend chega na Fase 1
            da spec v3.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-12 max-h-[calc(92vh-180px)] overflow-y-auto">
          {/* FORM ============================================ */}
          <div className="col-span-12 lg:col-span-8 p-6 space-y-7">
            {/* §1 — IDENTIDADE ----------------------------- */}
            <Section
              icon={<Sparkles className="w-4 h-4" />}
              title="Identidade"
              hint="Nome, descrição e ícone"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-1.5">
                  <Label>Nome *</Label>
                  <Input
                    placeholder='ex. "Feliz Natal"'
                    value={draft.name}
                    onChange={(e) => patch('name', e.target.value)}
                  />
                </div>

                <div className="col-span-2 space-y-1.5">
                  <Label>Descrição *</Label>
                  <Textarea
                    rows={2}
                    placeholder='ex. "Aprendendo até no dia de Natal."'
                    value={draft.description}
                    onChange={(e) => patch('description', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Texto público — aparece no perfil do usuário.
                  </p>
                </div>

                <div className="col-span-2 space-y-1.5">
                  <Label>Como conquistar *</Label>
                  <Input
                    placeholder='ex. "Completar 1 episódio em 25 de dezembro."'
                    value={draft.howToEarn}
                    onChange={(e) => patch('howToEarn', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Frase explicativa exibida no modal de desbloqueio.
                  </p>
                </div>

                <div className="col-span-2 space-y-2">
                  <Label>Tier</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {TIERS.map((t) => {
                      const active = draft.tier === t.value
                      return (
                        <button
                          key={t.value}
                          type="button"
                          onClick={() => changeTier(t.value)}
                          className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md border text-left transition-colors ${
                            active
                              ? 'border-primary bg-primary/10 text-foreground'
                              : 'border-border/60 hover:border-border bg-card/40 text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          <span className="bdg-tier-dot" data-tier={t.value} />
                          <div className="flex flex-col leading-tight">
                            <span className="text-sm font-medium">{t.label}</span>
                            <span className="text-[11px] opacity-70">{t.subtitle}</span>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Pontos</Label>
                  <Select
                    value={String(draft.points)}
                    onValueChange={(v) => patch('points', Number(v))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {POINT_OPTIONS.map((p) => (
                        <SelectItem key={p} value={String(p)}>
                          {p} pontos
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Default por tier: {TIERS.find((t) => t.value === draft.tier)?.defaultPoints}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label>Ordem na vitrine</Label>
                  <Input
                    type="number"
                    min={0}
                    value={draft.order}
                    onChange={(e) => patch('order', Number(e.target.value || 0))}
                  />
                </div>

                <div className="col-span-2 space-y-1.5">
                  <Label>Ícone (PNG transparente)</Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png"
                    className="hidden"
                    onChange={handleIconFileChange}
                    data-testid="badge-icon-input"
                  />
                  <div className="flex items-center justify-between gap-3 rounded-md border border-dashed border-border/60 bg-card/40 px-4 py-3">
                    <div className="flex items-center gap-3">
                      {draft.iconUrl ? (
                        <img
                          src={draft.iconUrl}
                          alt="Preview do ícone"
                          className="w-12 h-12 rounded object-contain bg-background/50"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded bg-card/60 border border-dashed border-border/40 flex items-center justify-center">
                          <Award className="w-5 h-5 text-muted-foreground/40" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {draft.iconUrl ? 'Ícone enviado' : 'Upload do ícone'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          PNG transparente · máx 5MB · resize 256×256 (alpha preservado)
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {draft.iconUrl && (
                        <Button
                          variant="ghost"
                          size="sm"
                          type="button"
                          onClick={() => patch('iconUrl', null)}
                          disabled={uploadMutation.isPending}
                        >
                          Remover
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        className="gap-1.5"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadMutation.isPending}
                      >
                        {uploadMutation.isPending ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Enviando…
                          </>
                        ) : (
                          <>
                            <Upload className="w-3.5 h-3.5" />
                            {draft.iconUrl ? 'Trocar' : 'Escolher arquivo'}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Section>

            <Separator className="bg-border/40" />

            {/* §2 — ESCOPO -------------------------------- */}
            <Section icon={<Shield className="w-4 h-4" />} title="Escopo" hint="Quem vê esta badge">
              <div className="grid grid-cols-2 gap-3">
                <ScopePin
                  active={draft.scope.type === 'global'}
                  title="Global"
                  description="Todas as empresas que usam a plataforma."
                  onClick={() => patch('scope', { type: 'global' } as Scope)}
                />
                <ScopePin
                  active={draft.scope.type === 'company'}
                  title="Empresa"
                  description="Apenas usuários daquela empresa."
                  // Default pra primeira empresa da lista; se ainda não
                  // carregou, deixa string vazia (select fica vazio).
                  onClick={() =>
                    patch('scope', {
                      type: 'company',
                      companyId: companies?.[0]?.id ?? '',
                    } as Scope)
                  }
                />
              </div>

              {draft.scope.type === 'company' && (
                <div className="space-y-1.5">
                  <Label>Empresa</Label>
                  <Select
                    value={draft.scope.companyId}
                    onValueChange={(v) => patch('scope', { type: 'company', companyId: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(companies ?? []).map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </Section>

            <Separator className="bg-border/40" />

            {/* §3 — CRITÉRIO ------------------------------ */}
            <Section
              icon={<Target className="w-4 h-4" />}
              title="Critério"
              hint="O que precisa acontecer"
            >
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select
                  value={draft.criterion.type}
                  onValueChange={(v) => changeCriterionType(v as CriterionType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CRITERION_GROUPS.map((g) => (
                      <SelectGroup key={g.group}>
                        <SelectLabel>{g.group}</SelectLabel>
                        {g.items.map((item) => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
                <CriterionHint type={draft.criterion.type} />
              </div>

              <CriterionParams
                criterion={draft.criterion}
                onChange={(c) => patch('criterion', c)}
              />

              {draft.criterion.type === 'manual_roster' && (
                <ManualRosterField
                  emails={recipientEmails}
                  onChange={setRecipientEmails}
                  isEditing={!!badge}
                />
              )}
            </Section>

            {/* Janela e Recorrência não se aplicam a manual_roster. */}
            {draft.criterion.type !== 'manual_roster' && (
              <>
                <Separator className="bg-border/40" />

                {/* §4 — JANELA -------------------------------- */}
                <Section
                  icon={<CalendarDays className="w-4 h-4" />}
                  title="Janela temporal"
                  hint="Quando vale"
                >
                  <div className="space-y-1.5">
                    <Label>Tipo</Label>
                    <Select
                      value={draft.window.type}
                      onValueChange={(v) => changeWindowType(v as WindowType)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WINDOW_OPTIONS.map((w) => (
                          <SelectItem key={w.value} value={w.value}>
                            {w.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <WindowHint type={draft.window.type} />
                  </div>

                  <WindowParams window={draft.window} onChange={(w) => patch('window', w)} />
                </Section>

                <Separator className="bg-border/40" />

                {/* §5 — RECORRÊNCIA --------------------------- */}
                <Section
                  icon={<Repeat className="w-4 h-4" />}
                  title="Recorrência"
                  hint="Quantas vezes pode ganhar"
                >
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {RECURRENCE_OPTIONS.map((r) => {
                      const allowed = isRecurrenceAllowed(r.value, draft.window.type)
                      const active = draft.recurrence === r.value
                      return (
                        <button
                          key={r.value}
                          type="button"
                          disabled={!allowed}
                          onClick={() => allowed && patch('recurrence', r.value as Recurrence)}
                          className={`flex flex-col items-start gap-1 px-3 py-3 rounded-md border text-left transition-colors ${
                            active
                              ? 'border-primary bg-primary/10 text-foreground'
                              : allowed
                                ? 'border-border/60 hover:border-border bg-card/40 text-muted-foreground hover:text-foreground'
                                : 'border-border/40 bg-card/20 text-muted-foreground opacity-40 cursor-not-allowed'
                          }`}
                        >
                          <span className="text-sm font-medium">{r.label}</span>
                          <span className="text-[11px] opacity-70 leading-tight">{r.hint}</span>
                        </button>
                      )
                    })}
                  </div>
                </Section>
              </>
            )}
          </div>

          {/* PREVIEW SIDEBAR ================================ */}
          <aside className="col-span-12 lg:col-span-4 bg-card/40 border-l border-border/40 p-6 lg:sticky lg:top-0 lg:self-start">
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Pré-visualização</span>
            </div>

            <div className="flex justify-center my-4">
              <BadgeMedallion
                tier={draft.tier}
                iconUrl={draft.iconUrl}
                inscription={inscription}
                size={200}
              />
            </div>

            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold text-foreground">
                {draft.name || 'Sem título'}
              </h3>
              <p className="text-xs text-muted-foreground min-h-[2.4em]">
                {draft.description || 'A descrição aparece aqui.'}
              </p>

              <div className="flex justify-center items-center gap-1.5 flex-wrap pt-1">
                <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border border-border/60 text-muted-foreground">
                  <span className="bdg-tier-dot" data-tier={draft.tier} />
                  {tierLabel[draft.tier]}
                </span>
                <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30 font-medium">
                  {draft.points} pts
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-border/60 text-muted-foreground">
                  {describeScopeLabel(draft.scope, companies)}
                </span>
              </div>
            </div>

            <Separator className="bg-border/40 my-5" />

            <div className="space-y-2">
              <SummaryRow
                icon={<Target className="w-3 h-3" />}
                label="Critério"
                value={describeCriterion(draft.criterion, quizzes, courses, modules, episodes)}
              />
              <SummaryRow
                icon={<CalendarDays className="w-3 h-3" />}
                label="Janela"
                value={describeWindow(draft.window)}
              />
              <SummaryRow
                icon={<Repeat className="w-3 h-3" />}
                label="Recorrência"
                value={RECURRENCE_OPTIONS.find((r) => r.value === draft.recurrence)?.label || '—'}
              />
              <SummaryRow
                icon={<Layers className="w-3 h-3" />}
                label="Estado"
                value={draft.active ? 'Ativa' : 'Inativa'}
              />
            </div>
          </aside>
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border/60 bg-background">
          <p className="text-xs text-destructive">
            {targetMissing ? 'Selecione o alvo do critério para salvar.' : ''}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={targetMissing}>
              {badge ? 'Atualizar badge' : 'Criar badge'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ================================================================= //
// AUX COMPONENTS                                                    //
// ================================================================= //

function Section({
  icon,
  title,
  hint,
  children,
}: {
  icon: React.ReactNode
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-md bg-primary/10 text-primary flex items-center justify-center">
          {icon}
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {hint && <p className="text-xs text-muted-foreground leading-tight">{hint}</p>}
        </div>
      </div>
      {children}
    </section>
  )
}

function ScopePin({
  active,
  title,
  description,
  onClick,
}: {
  active: boolean
  title: string
  description: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-start gap-1 px-4 py-3 rounded-md border text-left transition-colors ${
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border/60 hover:border-border bg-card/40 text-muted-foreground hover:text-foreground'
      }`}
    >
      <span className="text-sm font-medium">{title}</span>
      <span className="text-xs opacity-70">{description}</span>
    </button>
  )
}

function SummaryRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-border/30 last:border-0">
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {icon}
        {label}
      </span>
      <span className="text-xs text-foreground text-right max-w-[60%]">{value || '—'}</span>
    </div>
  )
}

function CriterionHint({ type }: { type: CriterionType }) {
  let hint = ''
  for (const g of CRITERION_GROUPS) {
    const found = g.items.find((i) => i.value === type)
    if (found) {
      hint = found.hint
      break
    }
  }
  if (!hint) return null
  return <p className="text-xs text-muted-foreground italic">→ {hint}</p>
}

function WindowHint({ type }: { type: WindowType }) {
  const hint = WINDOW_OPTIONS.find((w) => w.value === type)?.hint || ''
  return <p className="text-xs text-muted-foreground italic">→ {hint}</p>
}

/**
 * Painel do critério `quiz_perfect` com 3 selects encadeados —
 * Curso → Módulo → Quiz — pra evitar que o admin tenha que adivinhar
 * a que curso pertence cada quiz. Curso e módulo são apenas filtros
 * visuais (não viram parte do critério); só `quizId` (ou undefined)
 * vai pra `criterion`.
 *
 * Comportamentos:
 * - Sem curso selecionado → módulo e quiz mostram apenas "Qualquer".
 *   Pode-se publicar a badge como "Qualquer quiz 100%" (criterion.quizId
 *   undefined) — útil pra a badge genérica "Resposta Exata" do doc v1.0.
 * - Selecionou curso → módulos filtrados via useAdminModules(courseId);
 *   quizzes não-filtrados ainda (até escolher módulo).
 * - Selecionou módulo → quizzes filtrados client-side pelo `episodes.moduleId`.
 * - Quando o badge existente já tem `quizId` cadastrado, sincroniza
 *   curso e módulo automaticamente via lookup nos `episodes.modules`.
 */
function QuizPerfectParams({
  criterion,
  onChange,
  quizzes,
  quizzesLoading,
}: {
  criterion: Extract<Criterion, { type: 'quiz_perfect' }>
  onChange: (c: Criterion) => void
  quizzes: Quiz[] | undefined
  quizzesLoading: boolean
}) {
  const { data: courses, isLoading: coursesLoading } = useAdminCourses()

  // Sincroniza filtros visuais com o quizId já gravado (edição de badge).
  const currentQuiz = quizzes?.find((q) => String(q.id) === criterion.quizId)
  const initialCourseId = currentQuiz?.episodes?.modules?.courseId
  const initialModuleId = currentQuiz?.episodes?.moduleId

  const [selectedCourseId, setSelectedCourseId] = useState<number | undefined>(initialCourseId)
  const [selectedModuleId, setSelectedModuleId] = useState<number | undefined>(initialModuleId)

  // Quando a lista de quizzes chega via React Query e descobrimos o curso
  // pelo quizId existente, atualiza os selects auxiliares.
  useEffect(() => {
    if (initialCourseId !== undefined && selectedCourseId === undefined) {
      setSelectedCourseId(initialCourseId)
    }
    if (initialModuleId !== undefined && selectedModuleId === undefined) {
      setSelectedModuleId(initialModuleId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCourseId, initialModuleId])

  const { data: modules, isLoading: modulesLoading } = useAdminModules(selectedCourseId)

  // Quizzes filtrados pelo módulo (client-side — o back manda todos).
  const filteredQuizzes = quizzes?.filter((q) => {
    if (!selectedModuleId) return false // até escolher módulo, lista vazia
    return q.episodes?.moduleId === selectedModuleId
  })

  function setQuizId(quizId: string | undefined) {
    onChange({
      type: 'quiz_perfect',
      quizId,
      firstAttemptOnly: criterion.firstAttemptOnly,
    })
  }

  return (
    <div className="space-y-3">
      {/* CURSO */}
      <SimpleField label="Curso">
        <Select
          value={selectedCourseId !== undefined ? String(selectedCourseId) : '__any__'}
          onValueChange={(v) => {
            if (v === '__any__') {
              setSelectedCourseId(undefined)
              setSelectedModuleId(undefined)
              setQuizId(undefined) // qualquer quiz
            } else {
              setSelectedCourseId(Number(v))
              setSelectedModuleId(undefined)
              setQuizId(undefined) // reset quiz porque curso mudou
            }
          }}
          disabled={coursesLoading}
        >
          <SelectTrigger>
            <SelectValue placeholder={coursesLoading ? 'Carregando…' : 'Qualquer curso'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__any__">Qualquer curso (qualquer quiz)</SelectItem>
            {courses?.map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>
                {c.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SimpleField>

      {/* MÓDULO — habilitado só quando curso escolhido */}
      <SimpleField label="Módulo">
        <Select
          value={selectedModuleId !== undefined ? String(selectedModuleId) : '__any__'}
          onValueChange={(v) => {
            setSelectedModuleId(v === '__any__' ? undefined : Number(v))
            setQuizId(undefined) // reset quiz porque módulo mudou
          }}
          disabled={!selectedCourseId || modulesLoading}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={
                !selectedCourseId
                  ? 'Escolha um curso primeiro'
                  : modulesLoading
                    ? 'Carregando…'
                    : 'Selecione um módulo'
              }
            />
          </SelectTrigger>
          <SelectContent>
            {modules?.map((m) => (
              <SelectItem key={m.id} value={String(m.id)}>
                {m.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SimpleField>

      {/* QUIZ — habilitado só quando módulo escolhido */}
      <SimpleField label="Quiz">
        <Select
          value={criterion.quizId || '__any__'}
          onValueChange={(v) => setQuizId(v === '__any__' ? undefined : v)}
          disabled={!selectedModuleId || quizzesLoading}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={
                !selectedModuleId
                  ? 'Escolha um módulo primeiro'
                  : quizzesLoading
                    ? 'Carregando…'
                    : 'Selecione um quiz'
              }
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__any__">Qualquer quiz do módulo</SelectItem>
            {filteredQuizzes?.map((q) => (
              <SelectItem key={q.id} value={String(q.id)}>
                {q.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SimpleField>

      {/* Refinamento "1ª tentativa" — checkbox global do critério */}
      <label
        className="flex items-start gap-2.5 text-sm cursor-pointer select-none pt-1"
        data-testid="first-attempt-only-label"
      >
        <Checkbox
          checked={criterion.firstAttemptOnly === true}
          onCheckedChange={(checked) =>
            onChange({
              type: 'quiz_perfect',
              quizId: criterion.quizId,
              firstAttemptOnly: checked === true ? true : undefined,
            })
          }
          className="mt-0.5"
          data-testid="first-attempt-only-checkbox"
        />
        <span className="flex-1">
          <span className="font-medium">Apenas 1ª tentativa</span>
          <span className="block text-xs text-muted-foreground mt-0.5">
            Concede só quando o usuário gabarita o quiz na primeira tentativa (sem ter errado
            antes). Use pra badges tipo "Resposta Exata".
          </span>
        </span>
      </label>
    </div>
  )
}

/**
 * Selects encadeados Curso → Módulo → Episódio pros critérios que
 * apontam pra um conteúdo específico (`specific_course/module/episode`
 * e `module_all_quizzes_perfect`).
 *
 * Antes este painel era um único dropdown plano com TODOS os episódios
 * (ou módulos) de TODAS as empresas — inviável com catálogo grande.
 * Agora cada nível filtra o seguinte. Só o id do nível-alvo (`level`)
 * vira o `value`; curso/módulo dos níveis acima são filtros visuais.
 *
 * `allowAny` (usado pelo `module_all_quizzes_perfect`): adiciona a
 * opção "Qualquer" e mantém o select habilitado sem pai escolhido.
 *
 * Em modo edição, o id salvo é resolvido de volta (episódio → módulo →
 * curso) pra pré-selecionar os filtros pais.
 */
export function ContentTargetParams({
  level,
  value,
  onChange,
  allowAny = false,
}: {
  level: 'course' | 'module' | 'episode'
  value: string
  onChange: (value: string) => void
  allowAny?: boolean
}) {
  const { data: courses, isLoading: coursesLoading } = useAdminCourses()
  const { data: modules, isLoading: modulesLoading } = useAdminModules()
  const { data: episodes, isLoading: episodesLoading } = useAdminEpisodes()

  // Resolve curso/módulo pais a partir do id salvo (edição de badge).
  const resolved = useMemo(() => {
    const empty = {
      courseId: undefined as number | undefined,
      moduleId: undefined as number | undefined,
    }
    if (!value) return empty
    const idNum = Number(value)
    if (level === 'course') return { courseId: idNum, moduleId: undefined }
    if (level === 'module') {
      const m = modules?.find((mod) => mod.id === idNum)
      return { courseId: m?.courseId, moduleId: m?.id }
    }
    const ep = episodes?.find((e) => e.id === idNum)
    const m = ep ? modules?.find((mod) => mod.id === ep.moduleId) : undefined
    return { courseId: m?.courseId, moduleId: ep?.moduleId }
  }, [value, level, modules, episodes])

  const [selectedCourseId, setSelectedCourseId] = useState<number | undefined>(resolved.courseId)
  const [selectedModuleId, setSelectedModuleId] = useState<number | undefined>(resolved.moduleId)

  // As listas chegam async via React Query — quando o curso/módulo pai
  // do id salvo é descoberto, sincroniza os selects auxiliares.
  useEffect(() => {
    if (resolved.courseId !== undefined && selectedCourseId === undefined) {
      setSelectedCourseId(resolved.courseId)
    }
    if (resolved.moduleId !== undefined && selectedModuleId === undefined) {
      setSelectedModuleId(resolved.moduleId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved.courseId, resolved.moduleId])

  const filteredModules = modules?.filter((m) => m.courseId === selectedCourseId) ?? []
  const filteredEpisodes = episodes?.filter((e) => e.moduleId === selectedModuleId) ?? []

  return (
    <div className="space-y-3">
      {/* CURSO */}
      <SimpleField label="Curso">
        <Select
          value={
            selectedCourseId !== undefined ? String(selectedCourseId) : allowAny ? '__any__' : ''
          }
          onValueChange={(v) => {
            if (v === '__any__') {
              setSelectedCourseId(undefined)
              setSelectedModuleId(undefined)
              onChange('')
              return
            }
            setSelectedCourseId(Number(v))
            setSelectedModuleId(undefined)
            // O curso só é o alvo quando level === 'course'; nos demais
            // ele é filtro e o target (mais profundo) é resetado.
            onChange(level === 'course' ? v : '')
          }}
          disabled={coursesLoading}
        >
          <SelectTrigger>
            <SelectValue placeholder={coursesLoading ? 'Carregando…' : '— escolha um curso —'} />
          </SelectTrigger>
          <SelectContent>
            {allowAny && <SelectItem value="__any__">Qualquer curso</SelectItem>}
            {courses?.map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>
                {c.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SimpleField>

      {/* MÓDULO — pros níveis module/episode */}
      {(level === 'module' || level === 'episode') && (
        <SimpleField label="Módulo">
          <Select
            value={
              selectedModuleId !== undefined ? String(selectedModuleId) : allowAny ? '__any__' : ''
            }
            onValueChange={(v) => {
              if (v === '__any__') {
                setSelectedModuleId(undefined)
                onChange('')
                return
              }
              setSelectedModuleId(Number(v))
              onChange(level === 'module' ? v : '')
            }}
            disabled={modulesLoading || (!allowAny && !selectedCourseId)}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  !allowAny && !selectedCourseId
                    ? 'Escolha um curso primeiro'
                    : modulesLoading
                      ? 'Carregando…'
                      : '— escolha um módulo —'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {allowAny && <SelectItem value="__any__">Qualquer módulo</SelectItem>}
              {filteredModules.map((m) => (
                <SelectItem key={m.id} value={String(m.id)}>
                  {m.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SimpleField>
      )}

      {/* EPISÓDIO — só pro nível episode */}
      {level === 'episode' && (
        <SimpleField label="Episódio">
          <Select
            value={value || ''}
            onValueChange={(v) => onChange(v)}
            disabled={episodesLoading || !selectedModuleId}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  !selectedModuleId
                    ? 'Escolha um módulo primeiro'
                    : episodesLoading
                      ? 'Carregando…'
                      : '— escolha um episódio —'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {filteredEpisodes.map((ep) => (
                <SelectItem key={ep.id} value={String(ep.id)}>
                  {ep.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SimpleField>
      )}
    </div>
  )
}

function CriterionParams({
  criterion,
  onChange,
}: {
  criterion: Criterion
  onChange: (c: Criterion) => void
}) {
  // Lista de quizzes do back — chamada sempre (regra dos hooks) mas só
  // o painel `quiz_perfect` consome. React Query cacheia entre renders
  // e entre múltiplas abrturas do form (queryKey `admin-quizzes`).
  const { data: quizzes, isLoading: quizzesLoading } = useAdminQuizzes()

  switch (criterion.type) {
    case 'specific_episode':
      return (
        <ContentTargetParams
          key="specific_episode"
          level="episode"
          value={criterion.targetId}
          onChange={(v) => onChange({ type: 'specific_episode', targetId: v })}
        />
      )
    case 'specific_module':
      return (
        <ContentTargetParams
          key="specific_module"
          level="module"
          value={criterion.targetId}
          onChange={(v) => onChange({ type: 'specific_module', targetId: v })}
        />
      )
    case 'specific_course':
      return (
        <ContentTargetParams
          key="specific_course"
          level="course"
          value={criterion.targetId}
          onChange={(v) => onChange({ type: 'specific_course', targetId: v })}
        />
      )
    case 'count_episodes':
    case 'count_modules':
    case 'count_courses': {
      const noun =
        criterion.type === 'count_episodes'
          ? 'episódios'
          : criterion.type === 'count_modules'
            ? 'módulos'
            : 'cursos'
      return (
        <SimpleField label={`Quantidade de ${noun}`}>
          <Input
            type="number"
            min={1}
            value={criterion.count}
            onChange={(e) =>
              onChange({ ...criterion, count: Math.max(1, Number(e.target.value || 1)) })
            }
          />
        </SimpleField>
      )
    }
    case 'streak_days':
      return (
        <SimpleField label="Dias consecutivos">
          <Input
            type="number"
            min={2}
            value={criterion.count}
            onChange={(e) =>
              onChange({ type: 'streak_days', count: Math.max(2, Number(e.target.value || 2)) })
            }
          />
        </SimpleField>
      )
    case 'day_marathon':
      return (
        <SimpleField label="Episódios no mesmo dia">
          <Input
            type="number"
            min={2}
            value={criterion.count}
            onChange={(e) =>
              onChange({ type: 'day_marathon', count: Math.max(2, Number(e.target.value || 2)) })
            }
          />
        </SimpleField>
      )
    case 'quiz_perfect_streak':
      return (
        <SimpleField label="Quizzes 100% seguidos">
          <Input
            type="number"
            min={2}
            value={criterion.count}
            onChange={(e) =>
              onChange({
                type: 'quiz_perfect_streak',
                count: Math.max(2, Number(e.target.value || 2)),
              })
            }
          />
        </SimpleField>
      )
    case 'return_after_idle':
      return (
        <SimpleField label="Dias mínimos de ausência">
          <Input
            type="number"
            min={2}
            value={criterion.count}
            onChange={(e) =>
              onChange({
                type: 'return_after_idle',
                count: Math.max(2, Number(e.target.value || 2)),
              })
            }
          />
        </SimpleField>
      )
    case 'quiz_perfect':
      return (
        <QuizPerfectParams
          criterion={criterion}
          onChange={onChange}
          quizzes={quizzes}
          quizzesLoading={quizzesLoading}
        />
      )
    case 'module_all_quizzes_perfect':
      return (
        <ContentTargetParams
          key="module_all_quizzes_perfect"
          level="module"
          allowAny
          value={criterion.moduleId ?? ''}
          onChange={(v) =>
            onChange({ type: 'module_all_quizzes_perfect', moduleId: v || undefined })
          }
        />
      )
    default:
      return null
  }
}

function WindowParams({
  window: w,
  onChange,
}: {
  window: TemporalWindow
  onChange: (w: TemporalWindow) => void
}) {
  if (w.type === 'always') return null

  if (w.type === 'between') {
    return (
      <div className="grid grid-cols-2 gap-4">
        <SimpleField label="Início">
          <Input
            type="date"
            value={toDateInputValue(w.start)}
            onChange={(e) => onChange({ ...w, start: e.target.value })}
          />
        </SimpleField>
        <SimpleField label="Fim">
          <Input
            type="date"
            value={toDateInputValue(w.end)}
            onChange={(e) => onChange({ ...w, end: e.target.value })}
          />
        </SimpleField>
      </div>
    )
  }

  if (w.type === 'specific_date') {
    return (
      <SimpleField label="Data">
        <Input
          type="date"
          value={toDateInputValue(w.date)}
          onChange={(e) => onChange({ type: 'specific_date', date: e.target.value })}
        />
      </SimpleField>
    )
  }

  if (w.type === 'recurring_yearly') {
    const [month, day] = w.monthDay.split('-').map(Number)
    return (
      <div className="grid grid-cols-2 gap-4">
        <SimpleField label="Mês">
          <Select
            value={String(month)}
            onValueChange={(v) =>
              onChange({
                type: 'recurring_yearly',
                monthDay: `${String(Number(v)).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map((m, i) => (
                <SelectItem key={m} value={String(i + 1)}>
                  {String(i + 1).padStart(2, '0')} · {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SimpleField>
        <SimpleField label="Dia">
          <Input
            type="number"
            min={1}
            max={31}
            value={day}
            onChange={(e) =>
              onChange({
                type: 'recurring_yearly',
                monthDay: `${String(month).padStart(2, '0')}-${String(
                  Math.min(31, Math.max(1, Number(e.target.value || 1)))
                ).padStart(2, '0')}`,
              })
            }
          />
        </SimpleField>
      </div>
    )
  }

  if (w.type === 'recurring_monthly') {
    return (
      <SimpleField label="Dia do mês">
        <Input
          type="number"
          min={1}
          max={31}
          value={w.dayOfMonth}
          onChange={(e) =>
            onChange({
              type: 'recurring_monthly',
              dayOfMonth: Math.min(31, Math.max(1, Number(e.target.value || 1))),
            })
          }
        />
      </SimpleField>
    )
  }

  if (w.type === 'recurring_weekly') {
    return (
      <SimpleField label="Dia da semana">
        <div className="grid grid-cols-7 gap-1.5">
          {WEEKDAYS.map((wd) => {
            const active = w.weekday === wd.value
            return (
              <button
                key={wd.value}
                type="button"
                onClick={() => onChange({ type: 'recurring_weekly', weekday: wd.value })}
                className={`text-xs font-medium py-2 rounded-md border transition-colors ${
                  active
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border/60 hover:border-border bg-card/40 text-muted-foreground hover:text-foreground'
                }`}
              >
                {wd.short}
              </button>
            )
          })}
        </div>
      </SimpleField>
    )
  }

  return null
}

function SimpleField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  )
}

/**
 * Campo de upload do critério `manual_roster`. O admin sobe um arquivo
 * (.csv/.json/texto); o browser parseia os emails e devolve via
 * `onChange`. Em modo edição, a lista é incremental (concede a mais).
 */
function ManualRosterField({
  emails,
  onChange,
  isEditing,
}: {
  emails: string[]
  onChange: (emails: string[]) => void
  isEditing: boolean
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [rejected, setRejected] = useState<string[]>([])

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const text = await file.text()
    const parsed = parseRosterFile(text)
    onChange(parsed.emails)
    setRejected(parsed.rejected)
    setFileName(file.name)
    // Limpa pra permitir re-upload do mesmo arquivo.
    if (fileRef.current) fileRef.current.value = ''
  }

  return (
    <SimpleField label="Lista de destinatários (.csv ou .json)">
      <input
        ref={fileRef}
        type="file"
        accept=".csv,.json,.txt"
        className="hidden"
        onChange={handleFile}
        data-testid="roster-file-input"
      />
      <div className="flex items-center gap-3 rounded-md border border-dashed border-border/60 bg-card/40 px-4 py-3">
        <Button
          variant="outline"
          size="sm"
          type="button"
          className="gap-1.5 shrink-0"
          onClick={() => fileRef.current?.click()}
        >
          <Upload className="w-3.5 h-3.5" />
          Escolher arquivo
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-foreground truncate">
            {fileName ?? 'Nenhum arquivo selecionado'}
          </p>
          <p className="text-xs text-muted-foreground">
            Emails separados por vírgula, linha, ou em array JSON.
          </p>
        </div>
      </div>
      {emails.length > 0 && (
        <p className="text-xs text-emerald-400">
          {emails.length} email{emails.length > 1 ? 's' : ''} reconhecido
          {emails.length > 1 ? 's' : ''} no arquivo.
        </p>
      )}
      {rejected.length > 0 && (
        <p className="text-xs text-destructive">
          {rejected.length} ignorado{rejected.length > 1 ? 's' : ''} (não parecem email):{' '}
          {rejected.slice(0, 3).join(', ')}
          {rejected.length > 3 ? '…' : ''}
        </p>
      )}
      <p className="text-xs text-muted-foreground italic">
        {isEditing
          ? '→ Concede a badge a mais usuários. Quem já tem é ignorado.'
          : '→ Cada usuário cujo email casar recebe a badge na criação.'}
      </p>
    </SimpleField>
  )
}

function describeScopeLabel(scope: Scope, companies: Company[] | undefined): string {
  if (scope.type === 'company') {
    return companies?.find((c) => c.id === scope.companyId)?.name || 'Empresa'
  }
  return 'Global'
}

function describeCriterion(
  c: Criterion,
  quizzes?: Quiz[],
  courses?: Course[],
  modules?: Module[],
  episodes?: Episode[]
): string {
  switch (c.type) {
    case 'specific_episode':
      return `Episódio ${
        episodes?.find((e) => String(e.id) === c.targetId)?.title || c.targetId || '—'
      }`
    case 'specific_module':
      return `Módulo ${
        modules?.find((m) => String(m.id) === c.targetId)?.title || c.targetId || '—'
      }`
    case 'specific_course':
      return `Curso ${
        courses?.find((co) => String(co.id) === c.targetId)?.title || c.targetId || '—'
      }`
    case 'count_episodes':
      return `${c.count} episódios`
    case 'count_modules':
      return `${c.count} módulos`
    case 'count_courses':
      return `${c.count} cursos`
    case 'streak_days':
      return `${c.count} dias seguidos`
    case 'day_marathon':
      return `${c.count} eps no mesmo dia`
    case 'quiz_perfect_streak':
      return `${c.count} quizzes 100% seguidos`
    case 'return_after_idle':
      return `Retorno após ${c.count}+ dias`
    case 'any_activity':
      return 'Atividade no dia'
    case 'account_created':
      return 'Cadastro na plataforma'
    case 'quiz_perfect': {
      // Resolve título do quiz pelo id quando disponível; senão cai no id cru.
      const quizLabel = c.quizId
        ? quizzes?.find((q) => String(q.id) === c.quizId)?.title || `Quiz ${c.quizId}`
        : 'Qualquer quiz 100%'
      return c.firstAttemptOnly ? `${quizLabel} (1ª tentativa)` : quizLabel
    }
    case 'module_all_quizzes_perfect':
      return c.moduleId
        ? `Todos os quizzes de ${
            modules?.find((m) => String(m.id) === c.moduleId)?.title || `módulo ${c.moduleId}`
          }`
        : 'Todos quizzes de algum módulo'
    case 'quiz_comeback':
      return 'Errar e voltar a 100%'
    case 'manual_roster':
      return 'Lista manual de usuários'
  }
}

function describeWindow(w: TemporalWindow): string {
  switch (w.type) {
    case 'always':
      return 'Sempre vale'
    case 'between':
      return `${formatBrDateDashed(w.start)} → ${formatBrDateDashed(w.end)}`
    case 'specific_date':
      return formatBrDateDashed(w.date)
    case 'recurring_yearly': {
      const [m, d] = w.monthDay.split('-').map(Number)
      return `${String(d).padStart(2, '0')}-${String(m).padStart(2, '0')} todo ano`
    }
    case 'recurring_monthly':
      return `Dia ${w.dayOfMonth} de cada mês`
    case 'recurring_weekly': {
      const labels = ['', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado', 'domingo']
      return `Toda ${labels[w.weekday] || '—'}`
    }
  }
}

function isRecurrenceAllowed(recurrence: Recurrence, windowType: WindowType): boolean {
  if (recurrence === 'once') return true
  if (recurrence === 'per_year') return windowType === 'recurring_yearly' || windowType === 'always'
  if (recurrence === 'per_month')
    return windowType === 'recurring_monthly' || windowType === 'always'
  if (recurrence === 'per_week') return windowType === 'recurring_weekly' || windowType === 'always'
  return false
}
